import React, { useMemo, useEffect, useCallback } from 'react';
import { ASSET_TYPES_QUERY, POLICY_RENEWAL_LIST_QUERY } from '../queries';
import { INSURERS_QUERY } from 'features/insuranceDocument/edit/general/insuranceData/queries';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { usePolicyRenewalUtils } from './utils';
import { Card } from '@totalsoft/rocket-ui';
import ListBrowser, { CollectionType, getListBrowserStore } from '@ts-private/list-browser';
import PolicyRenewalListContainer from './list/PolicyRenewalListContainer';
import { useApolloFetching } from '@ts-private/list-browser-apollo-extensions';
import PolicyRenewalActions from './PolicyRenewalActions';
import ExportModalComponent from 'features/common/components/export/ExportModalComponent';

export const renewalStore = getListBrowserStore({
  name: 'renewal-browser',
  filterDefinitions: [
    {
      name: 'startDate',
      caption: 'Offer.PolicyRenewalList.Filter.DateFrom',
      initialValue: new Date(),
      type: 'date'
    },
    {
      name: 'endDate',
      caption: 'Offer.PolicyRenewalList.Filter.DateTo',
      initialValue: new Date(),
      type: 'date'
    },
    {
      name: 'insurerId',
      caption: 'Offer.PolicyRenewalList.Filter.InsurerId',
      type: 'select',
      underlyingType: 'number'
    },
    {
      name: 'contractNumber',
      caption: 'Offer.PolicyRenewalList.Filter.ContractNumber',
      type: 'text'
    },
    {
      name: 'clientUniqueId',
      caption: 'Offer.PolicyRenewalList.Filter.UniqueId',
      type: 'text'
    },
    {
      name: 'clientName',
      caption: 'Offer.PolicyRenewalList.Filter.ClientName',
      type: 'text'
    },
    {
      name: 'registrationNumber',
      caption: 'Offer.PolicyRenewalList.Filter.RegistrationNumber',
      type: 'text'
    },
    {
      name: 'chassisNumber',
      caption: 'Offer.PolicyRenewalList.Filter.ChassisNumber',
      type: 'text'
    },
    {
      name: 'assetType',
      caption: 'Offer.PolicyRenewalList.Filter.AssetType',
      type: 'select',
      underlyingType: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'Offer.PolicyRenewalList.Field.FinishDate',
      value: 'FinishDate'
    },
    {
      caption: 'Offer.PolicyRenewalList.Field.Insured',
      value: 'Insured'
    },
    {
      caption: 'Offer.PolicyRenewalList.Filter.User',
      value: 'User'
    },
    {
      caption: 'Offer.PolicyRenewalList.Field.Model',
      value: 'Model'
    },
    {
      caption: 'Offer.PolicyRenewalList.Field.ContractState',
      value: 'ContractState'
    }
  ],
  pager: {
    orderBy: 'FinishDate',
    direction: 'desc'
  }
});

const PolicyRenewalListBrowserContainer = () => {
  const { retrieveAvailableItems, replaceAvailableItems, addAvailableItems, clearUnselectedItems } = usePolicyRenewalUtils();

  // Clear selections that are still in cache from the last time the renewal page was accessed
  useEffect(() => {
    replaceAvailableItems([]);
  }, [replaceAvailableItems]);

  const handleRenewalPolicyDataRetrieved = useCallback(
    data => {
      clearUnselectedItems();
      if (!data?.renewalPolicies?.values || !data?.renewalPolicies?.renewalPolicyIds) return;

      // Filter out selections that are not among the new items
      const availableItems = retrieveAvailableItems();
      replaceAvailableItems(availableItems.filter(x => data.renewalPolicies.renewalPolicyIds.includes(x.renewalDocumentId)));

      addAvailableItems(data.renewalPolicies.values);
    },
    [addAvailableItems, clearUnselectedItems, replaceAvailableItems, retrieveAvailableItems]
  );

  const renewalFetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: POLICY_RENEWAL_LIST_QUERY,
        name: 'renewalPolicies',
        fetchPolicy: 'network-only',
        onCompleted: handleRenewalPolicyDataRetrieved
      },
      collectionsOptions: [
        {
          queryOptions: { query: INSURERS_QUERY, variables: { insuranceTypeId: 3 } },
          name: 'insurers',
          identifier: 'insurerId',
          collectionType: CollectionType.Filter,
          valueKey: 'chPartnerId',
          labelKey: 'name'
        },
        {
          queryOptions: { query: ASSET_TYPES_QUERY },
          name: 'assetTypes',
          identifier: 'assetType',
          collectionType: CollectionType.Filter,
          valueKey: 'value',
          labelKey: 'value'
        }
      ]
    }),
    [handleRenewalPolicyDataRetrieved]
  );

  useApolloFetching(renewalStore, renewalFetchingOptions);

  return (
    <Card icon={ListAltIcon}>
      <ListBrowser store={renewalStore} ItemsComponent={PolicyRenewalListContainer} floatingActions />
      <ExportModalComponent
        store={renewalStore}
        options={{ queryOptions: { query: POLICY_RENEWAL_LIST_QUERY, name: 'renewalPolicies' }, maxSize: 1000 }}
      />
      <PolicyRenewalActions />
    </Card>
  );
};

export default PolicyRenewalListBrowserContainer;
